import { NotificationApi } from '@liks/backend-svc-api/esm/apis/NotificationApi';
import type { LiveEvent } from '../types/liveEvent';
import { backendApiConfiguration } from './backendApi';
import { feedLiveEventStreamBuffer } from './liveEventStream';

export type LiveEventsSubscribeHandlers = {
  onOpen?: () => void;
  onEvent: (event: LiveEvent) => void;
};

class LiveEventsService {
  private readonly notificationApi = new NotificationApi(
    backendApiConfiguration,
  );

  async subscribe(
    handlers: LiveEventsSubscribeHandlers,
    signal?: AbortSignal,
  ): Promise<void> {
    const response =
      await this.notificationApi.deLiksLiveeventBoundaryLiveEventResourceSubscribeLiveEventsRaw(
        {},
        async ({ init }) => ({
          ...init,
          signal,
          headers: {
            ...init.headers,
            Accept: 'text/event-stream, application/json',
          },
        }),
      );

    if (signal?.aborted) {
      return;
    }

    handlers.onOpen?.();

    const body = response.raw.body;
    if (!body) {
      return;
    }

    const reader = body.getReader();
    const decoder = new TextDecoder();
    let pendingBuffer = '';

    try {
      for (;;) {
        const { value, done } = await reader.read();
        if (done) {
          break;
        }

        pendingBuffer += decoder.decode(value, { stream: true });
        pendingBuffer = feedLiveEventStreamBuffer(
          pendingBuffer,
          handlers.onEvent,
        );

        if (signal?.aborted) {
          await reader.cancel();
          return;
        }
      }

      pendingBuffer += decoder.decode();
      feedLiveEventStreamBuffer(pendingBuffer, handlers.onEvent);
    } catch (error) {
      if (signal?.aborted) {
        return;
      }
      throw error;
    }
  }
}

export const liveEventsService = new LiveEventsService();
